import { skipToken } from '@reduxjs/toolkit/query'
import { useGetOrderRatingRecentActivitiesQuery } from 'api/orderRatings'
import type { OrderRatingDetail } from 'api/orderRatings.types'
import type { ActivityStatus } from 'api/recoveryAi.types'
import { useGetUserQuery } from 'api/users'
import { CustomerInfo } from 'components/CustomerInfoCard'
import { RecentActivityRow } from 'components/RecentActivitiesTable'
import { CustomerEmailState } from 'components/SendMessageModal'
import { buildTranslate } from 'locales'
import { useMemo } from 'react'
import { customerProfileUrl } from 'utilities/urlUtils'
import { formatDate } from './ratingFormatters'
import { resolutionStatusFor } from './resolutionStatus'
import { getViewPath } from './useOrderRatings'

const t = buildTranslate('order_ratings.rating_detail')

// The Recent-activities column shows no word for an unresolved rating, so it
// maps the shared resolution vocabulary's `unresolved` onto null (the other
// two states pass through: ai → 'resolved', operator → 'closed_by_operator').
const recentRowResolutionType = (
  kind: OrderRatingDetail['resolvedBy']['kind']
): ActivityStatus | null => {
  const status = resolutionStatusFor(kind)
  return status === 'unresolved' ? null : status
}

// The Recent-activities panel shows the first 5 and reveals the rest behind its
// "View N more" expander, over the rows it ALREADY has — there is no second
// query and no pager, so this is a hard reachability ceiling, not just a first
// page. Anything past row 25 of a customer's history cannot be reached from the
// panel at all. That ceiling used to be academic because the panel called the
// thumbs-down, conversation-less index; now that it gets a customer's whole
// history, a heavy customer can plausibly exceed it. The backend caps per_page
// at 100 — raise this, or give the panel real paging, if that starts to bite.
const RECENT_RATINGS_PER_PAGE = 25

// The two customer-facing panels of the ratings-detail scene: the customer info
// card and the recent-activities table, plus the Send-message recipient state.
export type RatingCustomerPanels = {
  customerInfo: CustomerInfo | null
  profileUrl: string | null
  recentRatingRows: RecentActivityRow[]
  customerEmailState: CustomerEmailState
}

// Owns the two customer-scoped queries (the user record and this customer's
// recent ratings) and derives the customer card, the profile link, the
// recent-activities rows, and the Send-message recipient state. Tolerates a
// null rating (both queries skip, the card is null, the rows empty) so the
// composer can call it unconditionally, ahead of its guards (rules of hooks).
export const useRatingCustomerPanels = (
  rating: OrderRatingDetail | null,
  ratingId: string
): RatingCustomerPanels => {
  // The rating's customer — known from the handed-over row the instant the
  // operator clicks, so the customer card below doesn't wait on the detail
  // fetch. Null on a rating whose user record is gone (or a backend without the
  // field yet), which skips the user query and omits the card. The
  // recent-activities query no longer reads either: that route derives the
  // customer from the rating id server-side.
  const userId = rating?.userId ?? null
  const userUid = rating?.userUid ?? null

  // The customer panel is fed by the SAME endpoint the customer-profile page
  // uses, so the two never disagree — keyed by users.identifier (uid). skipToken
  // keeps the query from running at all until the uid is known.
  // currentData, not data: scoped to the current uid — RTK's data retains the
  // prior customer while the next fetch is in flight; on pager nav that painted
  // the previous customer against the new rating. Mirrors the rating query.
  const { currentData: userData, isFetching: isFetchingUser } = useGetUserQuery(
    userUid ?? skipToken
  )
  const profile = userData?.profile ?? null

  const customerInfo: CustomerInfo | null = useMemo(
    () =>
      profile
        ? {
            firstName: profile.first_name ?? null,
            lastName: profile.last_name ?? null,
            email: profile.email ?? null,
            phone: profile.phone ?? null,
            // The profile's display tier (what Users/View's TierBadge shows),
            // falling back to the raw tier token when there's no current tier.
            loyaltyTier: profile.current_tier ?? profile.tier ?? null,
            totalVisits: profile.lifetime_purchases,
            // Already dollars on this payload — see CustomerInfo.
            lifetimeSpend: profile.lifetime_spend,
            // Uncapped lifetime count of the customer's resolved order-feedback
            // cases (nexus AI-1026). Null on a pre-field backend → em-dash.
            priorResolutionCount: profile.prior_resolution_count ?? null,
          }
        : null,
    [profile]
  )

  // This customer's WHOLE feedback history, newest first — both thumbs,
  // conversation-backed or not, with the viewed rating already excluded
  // server-side. Keyed on the ROUTE param rather than the fetched record, so it
  // starts the instant the page mounts instead of waiting on the detail fetch
  // for a user id.
  // currentData, not data: scoped to the current rating id — RTK's data retains
  // the prior rating's history while the next fetch is in flight, which on
  // pager nav painted the previous customer's activity against the new rating.
  const { currentData: recentData } = useGetOrderRatingRecentActivitiesQuery(
    ratingId
      ? { id: ratingId, page: 1, per_page: RECENT_RATINGS_PER_PAGE }
      : skipToken
  )

  const recentRatingRows: RecentActivityRow[] = useMemo(
    () =>
      (recentData?.data ?? []).map(recent => ({
        id: recent.id,
        date: recent.date,
        // resolutionType is the generic fallback (resolved / closed / em-dash);
        // the specific reward name (AI-1025) below is preferred by the cell
        // when present. Shares resolutionStatusFor with the header badge and
        // the timeline: an AI close is a real resolution ("Resolved"), but an
        // operator's close is neutral ("Closed"), and the column has no word
        // for unresolved, so that maps to null.
        resolutionType: recentRowResolutionType(recent.resolvedBy.kind),
        rewardName: recent.rewardName,
        resolvedBy: recent.resolvedBy,
        // Real per-row value, and now genuinely mixed: this route returns the
        // customer's thumbs-UP ratings too, which the old index-backed query
        // could never produce.
        rating: recent.rating,
        // AI-1032, DELIBERATELY WITHHELD. The backend does compute a verdict on
        // this route (OrderRatings::CalculateReturns), but its anchor is the
        // rating's prompted_at rather than the order date, and the "no return"
        // arm can't distinguish a real no-show from a purchase-ingestion gap —
        // so the numbers are not trusted yet. RecentActivitiesTable gates the
        // whole column on this field being PRESENT on some row (never on it
        // being truthy — a real `false` must still show it), so not forwarding
        // it hides the column with none of its rendering code removed. Restore
        // by passing `recent.returnedWithin30Days` here; nothing else changes.
        // Tracked in AI-1058, which records both data concerns in full.
        returnedWithin30Days: undefined,
        // Shared with the Details table's own View links, so the two surfaces
        // can't drift: a conversation-backed row goes STRAIGHT to its RAI case
        // rather than to the rating detail page, which would only serve a
        // minimal redirect payload and bounce there anyway.
        viewTo: getViewPath(recent),
        viewAriaLabel: t('recent_activities.view_aria', {
          date: formatDate(recent.date),
        }),
      })),
    [recentData]
  )

  // The recipient's four states (see SendMessageModal's CustomerEmailState):
  // block Send only when there's genuinely no linked user (`absent`) or the
  // profile is still loading (`loading`). A rating whose user exists but whose
  // email we can't display is `masked`: the backend resolves the address
  // server-side, so it still delivers and Send stays enabled.
  const customerEmailState = ((): CustomerEmailState => {
    if (customerInfo?.email) {
      return { status: 'known', value: customerInfo.email }
    }
    if (userId === null) return { status: 'absent' }
    if (userUid != null && isFetchingUser && !userData) {
      return { status: 'loading' }
    }
    return { status: 'masked' }
  })()

  return {
    customerInfo,
    profileUrl: profile ? customerProfileUrl(profile.uid) : null,
    recentRatingRows,
    customerEmailState,
  }
}
